import { Vector3 } from "three";
import type { HandFrame } from "./types";
import { FilesetResolver, HandLandmarker } from "@mediapipe/tasks-vision";

export class HandTrackingService {
  private _handLandmarker?: HandLandmarker;
  private _video!: HTMLVideoElement;
  private _previewContainer!: HTMLDivElement;
  private _lastVideoTime: number = 0;
  private _lastInferenceTime: number = 0;
  private _inferenceThrottleMs: number = 40;
  private _initialized: boolean = false;

  public frame: HandFrame = {
    hasHand: false,
    pinch: false,
    nx: 0,
    ny: 0,
    worldX: 0,
    worldY: 0,
    worldZ: 0,
    vx: 0,
    vy: 0,
    vz: 0,
  };

  private _prevWorldPos: Vector3 = new Vector3();
  private _isTouchMode: boolean = false;
  private _touchX: number = 0;
  private _touchY: number = 0;
  private _isPointerDown: boolean = false;
  private _pinchThreshold: number = 0.05;

  constructor() {
    this.createPreviewElements();
    this.setupTouchListeners();
    this.initMediaPipe();
  }

  private createPreviewElements() {
    this._previewContainer = document.createElement("div");
    this._previewContainer.id = "camera-preview";
    this._previewContainer.style.cssText = `
      position: fixed;
      bottom: 90px;
      right: 20px;
      width: 160px;
      height: 120px;
      z-index: 1000;
      display: none;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
    `;

    this._video = document.createElement("video");
    this._video.id = "webcam-preview";
    this._video.autoplay = true;
    this._video.playsInline = true;
    this._video.muted = true;
    this._video.style.cssText = `
      width: 100%;
      height: 100%;
      object-fit: cover;
      transform: scaleX(-1);
      background: #000;
    `;

    this._previewContainer.appendChild(this._video);
    document.body.appendChild(this._previewContainer);
  }

  private setupTouchListeners() {
    const handlePointerDown = (e: PointerEvent) => {
      // Ignore if clicking on UI buttons
      if ((e.target as HTMLElement).closest('button, .ui-button, .screen-content')) return;
      this._isPointerDown = true;
      this._touchX = e.clientX;
      this._touchY = e.clientY;
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (this._isPointerDown) {
        this._touchX = e.clientX;
        this._touchY = e.clientY;
      }
    };

    const handlePointerUp = () => {
      this._isPointerDown = false;
    };

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
  }

  private async initMediaPipe() {
    try {
      console.log("[HandTracking] Initializing MediaPipe...");
      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
      );

      this._handLandmarker = await HandLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: "https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/latest/hand_landmarker.task",
          delegate: "GPU"
        },
        numHands: 1,
        runningMode: "VIDEO"
      });
      this._initialized = true;
      console.log("[HandTracking] MediaPipe initialized!");
    } catch (error) {
      console.error("[HandTracking] Failed to initialize MediaPipe:", error);
    }
  }

  async startSelfieCamera() {
    if (this._isTouchMode) return;

    try {
      console.log("[HandTracking] Starting camera...");
      const constraints = {
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30, max: 30 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      this._video.srcObject = stream;
      await this._video.play();
      this._previewContainer.style.display = "block";
      console.log("[HandTracking] Camera started!");
    } catch (error) {
      console.warn("[HandTracking] Camera access denied, switching to touch mode:", error);
      this.enableTouchMode();
    }
  }

  public enableTouchMode() {
    this._isTouchMode = true;
    if (this._previewContainer) {
      this._previewContainer.style.display = "none";
    }
  }

  stopCamera() {
    if (this._video && this._video.srcObject) {
      const mediaStream = this._video.srcObject as MediaStream;
      const tracks = mediaStream.getTracks();
      tracks.forEach((track: MediaStreamTrack) => track.stop());
      this._video.srcObject = null;
    }
    this._previewContainer.style.display = "none";
  }

  togglePreview() {
    if (this._isTouchMode) return;
    this._previewContainer.style.display = 
      this._previewContainer.style.display === "none" ? "block" : "none";
  }

  update(dt: number): void {
    if (this._isTouchMode) {
      this.updateTouchFrame(dt);
      return;
    }

    if (!this._video || !this._handLandmarker || !this._initialized || this._video.readyState < 2) {
      this.frame.hasHand = false;
      this.frame.pinch = false;
      return;
    }

    const now = performance.now();

    if (now - this._lastInferenceTime < this._inferenceThrottleMs) {
      return;
    }

    if (this._video.currentTime !== this._lastVideoTime) {
      try {
        const detections = this._handLandmarker.detectForVideo(this._video, now);
        this.processResults(detections, dt);
        this._lastVideoTime = this._video.currentTime;
        this._lastInferenceTime = now;
      } catch (e) {
        console.error("[HandTracking] Detection error:", e);
      }
    }
  }

  private updateTouchFrame(dt: number) {
    if (this._isPointerDown) {
      this.frame.hasHand = true;
      this.frame.pinch = true;
      const worldPos = this.screenToWorld(this._touchX, this._touchY);
      this.frame.nx = this._touchX / window.innerWidth;
      this.frame.ny = 1 - (this._touchY / window.innerHeight);
      this.frame.worldX = worldPos.x;
      this.frame.worldY = worldPos.y;
      this.frame.worldZ = worldPos.z;
      this.computeVelocity(worldPos, dt);
    } else {
      this.frame.hasHand = false;
      this.frame.pinch = false;
    }
  }

  private processResults(results: any, dt: number) {
    if (!results.landmarks || results.landmarks.length === 0) {
      this.frame.hasHand = false;
      this.frame.pinch = false;
      return;
    }

    const landmarks = results.landmarks[0];
    if (landmarks.length < 21) {
      this.frame.hasHand = false;
      this.frame.pinch = false;
      return;
    }

    const thumbTip = landmarks[4];
    const indexTip = landmarks[8];

    const dx = thumbTip.x - indexTip.x;
    const dy = thumbTip.y - indexTip.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    this.frame.hasHand = true;
    this.frame.pinch = distance < this._pinchThreshold;

    // Mirror X coordinate for selfie camera
    const mx = 1 - landmarks[9].x;
    const my = 1 - landmarks[9].y;

    this.frame.nx = mx;
    this.frame.ny = my;

    const worldPos = this.normalizedToWorld(mx, my);
    this.frame.worldX = worldPos.x;
    this.frame.worldY = worldPos.y;
    this.frame.worldZ = worldPos.z;

    this.computeVelocity(worldPos, dt);
  }

  private computeVelocity(currentWorldPos: Vector3, dt: number) {
    if (dt > 0) {
      this.frame.vx = (currentWorldPos.x - this._prevWorldPos.x) / dt;
      this.frame.vy = (currentWorldPos.y - this._prevWorldPos.y) / dt;
      this.frame.vz = (currentWorldPos.z - this._prevWorldPos.z) / dt;
    }
    this._prevWorldPos.copy(currentWorldPos);
  }

  private screenToWorld(screenX: number, screenY: number): Vector3 {
    const width = window.innerWidth;
    const height = window.innerHeight;

    const nx = screenX / width;
    const ny = screenY / height;

    return this.normalizedToWorld(nx, 1 - ny);
  }

  private normalizedToWorld(nx: number, ny: number): Vector3 {
    const worldPos = new Vector3();
    // Map normalized coords to world space
    // X: -4 to 4, Y: -1 to 3, Z: fixed at 2 (in front of camera)
    worldPos.set(
      (nx - 0.5) * 8,  // -4 to 4
      (ny - 0.5) * 4 + 1,  // -1 to 3
      2
    );
    return worldPos;
  }

  destroy() {
    this.stopCamera();
    if (this._previewContainer && this._previewContainer.parentNode) {
      this._previewContainer.parentNode.removeChild(this._previewContainer);
    }
    if (this._handLandmarker) {
      this._handLandmarker.close();
    }
  }
}
